// Startseite: Signatur-Momente mit GSAP + ScrollTrigger (nur Template index).
// Signatur-Moment 1 (Hero) läuft rein über CSS in sections/tg-hero.liquid.
// - GSAP lädt erst nach dem ersten Bildaufbau (requestIdleCallback) und nie bei reduzierter Bewegung.
// - Schlägt das Laden fehl, bleibt die Seite unverändert nutzbar (alles ist ohne JS sichtbar).
// - Nur transform und opacity; ab 990 px scrollt Horizon in .page-wrapper statt im Fenster.
const motion = matchMedia('(prefers-reduced-motion: reduce)');

// Start erst nach dem tatsächlichen ersten Bildaufbau (Paint-Timing) UND nach load, dann im Leerlauf.
// load allein reicht nicht: Er kann vor dem ersten Paint feuern.
function afterFirstPaint() {
  return new Promise((resolve) => {
    if (performance.getEntriesByName('first-contentful-paint').length) return resolve();
    try {
      new PerformanceObserver((list, observer) => {
        if (list.getEntriesByName('first-contentful-paint').length) {
          observer.disconnect();
          resolve();
        }
      }).observe({ type: 'paint', buffered: true });
    } catch (error) {
      resolve();
    }
    setTimeout(resolve, 4000);
  });
}

function afterLoad() {
  return document.readyState === 'complete' ? Promise.resolve() : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
}

async function whenIdle(callback) {
  await Promise.all([afterFirstPaint(), afterLoad()]);
  if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 3000 });
  else setTimeout(callback, 1500);
}

// GSAP liegt als UMD vor und setzt beim Start self.window – im Strict Mode eines Moduls wirft das.
// Deshalb als klassisches Skript nachladen; die URL kommt aus Horizons Importmap.
function loadScript(specifier) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = import.meta.resolve(specifier);
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
}

async function loadGsap() {
  await loadScript('@theme/gsap');
  await loadScript('@theme/gsap-scrolltrigger');
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger) throw new Error('GSAP nicht verfügbar');
  gsap.registerPlugin(ScrollTrigger);
  return { gsap, ScrollTrigger };
}

function scrollerFor(wrapper) {
  return wrapper && getComputedStyle(wrapper).overflowY !== 'visible' && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;
}

function isPast(element, scroller) {
  const top = element.getBoundingClientRect().top;
  const viewport = scroller === window ? window.innerHeight : scroller.clientHeight;
  return top < viewport;
}

/* Signatur-Moment 2: Qualität – ab 990 px gepinnt (natives sticky, max. 0,9 Bildschirmhöhen).
   Die Kennzahl "2×" bleibt unverändert. Der grüne Balken wächst von der Länge des Norm-Balkens auf die doppelte
   (scaleX 0,5 → 1): ab 990 px an den Scrollweg gekoppelt, darunter einmalig beim Einblenden.
   Schritte leuchten nacheinander auf und bleiben hell, der grüne Stein markiert den neuesten Schritt, Gitter driftet. */
function quality(gsap, ScrollTrigger, section, scroller) {
  const steps = [...section.querySelectorAll('[data-tg-quality-step]')];
  const pattern = section.querySelector('[data-tg-quality-pattern]');
  const bar = section.querySelector('[data-tg-bar]');

  // Hintergrund driftet leicht (alle Breiten)
  if (pattern) {
    gsap.fromTo(pattern, { y: -24 }, { y: 24, ease: 'none', scrollTrigger: { trigger: section, scroller, start: 'top bottom', end: 'bottom top', scrub: true } });
  }

  // Pinning nur ab 990 px und nur, wenn die Section noch nicht erreicht ist (sonst würde Inhalt springen)
  const pinned = matchMedia('(min-width: 990px)').matches && !isPast(section, scroller);

  // Ohne Pinning: Balken wächst einmalig beim Einblenden – nur wenn er noch unterhalb liegt (sonst bleibt der Endzustand)
  if (bar && !pinned && !isPast(bar, scroller)) {
    gsap.fromTo(bar, { scaleX: 0.5 }, { scaleX: 1, duration: 0.9, ease: 'power2.out', scrollTrigger: { trigger: bar, scroller, start: 'top 90%', once: true } });
  }
  if (!pinned) return;

  // Scrollweg erst jetzt reservieren: Die Section liegt noch unterhalb, sichtbarer Inhalt verschiebt sich nicht
  section.classList.add('tg-gsap', 'tg-pin');
  ScrollTrigger.refresh();
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      scroller,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.6,
    },
  });

  if (bar) timeline.fromTo(bar, { scaleX: 0.5 }, { scaleX: 1, duration: 0.6, ease: 'none' }, 0);

  // Nicht aktive Schritte: 85 % Deckkraft (Kontrast mind. 4,5:1), leicht versetzt
  const accents = steps.map((step) => step.querySelector('.tg-stone__accent'));
  gsap.set(steps, { opacity: 0.85, x: -8 });
  gsap.set(accents, { opacity: 0 });
  steps.forEach((step, index) => {
    const at = 0.25 + index * 0.35;
    timeline.to(step, { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out' }, at);
    if (accents[index]) timeline.to(accents[index], { opacity: 1, duration: 0.2 }, at);
    if (index > 0 && accents[index - 1]) timeline.to(accents[index - 1], { opacity: 0, duration: 0.2 }, at);
  });
}

/* Signatur-Moment 3: Kategorien – Bilder bewegen sich leicht im Rahmen (Kacheln erscheinen über tg-reveal gestaffelt) */
function categories(gsap, section, scroller) {
  for (const image of section.querySelectorAll('.tg-categories__media img')) {
    gsap.fromTo(
      image,
      { yPercent: -5, scale: 1.12 },
      { yPercent: 5, scale: 1.12, ease: 'none', scrollTrigger: { trigger: image.parentElement, scroller, start: 'top bottom', end: 'bottom top', scrub: true } }
    );
  }
}

// Alles zurück auf den Ausgangszustand (Endzustand ohne Animation)
function teardown(gsap, ScrollTrigger) {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill(true));
  gsap.globalTimeline.clear();
  gsap.set('[data-tg-quality-step], [data-tg-bar], .tg-stone__accent, .tg-categories__media img, [data-tg-quality-pattern]', { clearProps: 'all' });
  document.querySelector('[data-tg-quality]')?.classList.remove('tg-pin', 'tg-gsap');
}

async function init() {
  if (motion.matches) return;
  const qualitySection = document.querySelector('[data-tg-quality]');
  const categorySection = document.querySelector('.tg-categories');
  if (!qualitySection && !categorySection) return;

  let lib;
  try {
    lib = await loadGsap();
  } catch (error) {
    return; // Seite bleibt vollständig nutzbar
  }
  const { gsap, ScrollTrigger } = lib;

  // Ab 990 px scrollt .page-wrapper, darunter das Fenster: Scroller und Pinning hängen vom Breakpoint ab
  const setup = () => {
    const scroller = scrollerFor(document.querySelector('.page-wrapper'));
    ScrollTrigger.defaults({ scroller });
    if (qualitySection) quality(gsap, ScrollTrigger, qualitySection, scroller);
    if (categorySection) categories(gsap, categorySection, scroller);
  };
  setup();

  // Fenstergröße über 990 px hinweg geändert: Animationen neu aufbauen (nach dem Umbruch des Layouts)
  matchMedia('(min-width: 990px)').addEventListener('change', () => {
    if (motion.matches) return;
    teardown(gsap, ScrollTrigger);
    requestAnimationFrame(() => requestAnimationFrame(setup));
  });

  motion.addEventListener('change', () => {
    if (motion.matches) teardown(gsap, ScrollTrigger);
  });
}

whenIdle(init);
